import { useMemo, useState } from 'react';
import { ArrowLeftIcon, Pencil } from 'lucide-react';
import { cn } from '@/lib/utils';
import { CustomModal } from '@/shared/components/ui/modal';
import { usePageStore } from '@/features/pages/stores/usePageStore';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/shared/components/ui/tooltip';
import { useCollectionStore } from '../../stores/useCollectionStore';
import { useCollectionActions } from '../../hooks/useCollectionActions';
import CollectionTitle from './Title';
import type { Collection } from '../../types/collection.types';

type CollectionDetailHeaderProps = {
  collection?: Collection | null;
  disabled?: boolean;
  onBackClick: () => void;
};

const CollectionDetailHeader = ({
  collection,
  onBackClick,
  disabled = false,
}: CollectionDetailHeaderProps) => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const editable = usePageStore(state => state.editable);
  const storedCurrentCollection = useCollectionStore(state => state.currentCollection);
  const currentCollection = collection ?? storedCurrentCollection;
  const canWrite = useMemo(
    () => !disabled && editable && !currentCollection?.archivedAt,
    [currentCollection?.archivedAt, disabled, editable]
  );

  if (!currentCollection) return null;

  return (
    <>
      <div className="w-full flex items-center gap-2 px-3 pr-10 mb-2">
        <button
          title="Back to collections"
          onClick={onBackClick}
          className={cn(
            'h-6 w-6 rounded-sm cursor-pointer',
            'text-foreground transition-opacity hover:opacity-70'
          )}
        >
          <ArrowLeftIcon className="h-5 w-5" />
        </button>
        <div className="ml-[-4px] flex min-w-0 flex-1 items-center gap-1.5 overflow-hidden">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div
                  className={cn(
                    'min-w-0 truncate py-4 text-[16px] font-bold outline-none',
                    currentCollection.archivedAt && 'line-through'
                  )}
                >
                  {currentCollection.title}
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p>{currentCollection.title}</p>
              </TooltipContent>
            </Tooltip>
            {canWrite && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={() => {
                      setIsModalOpen(true);
                    }}
                    className={cn(
                      'inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-sm',
                      'cursor-pointer text-muted-foreground transition-colors hover:text-foreground'
                    )}
                  >
                    <Pencil className="h-3 w-3 opacity-20 hover:opacity-100" />
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Edit collection</p>
                </TooltipContent>
              </Tooltip>
            )}
          </TooltipProvider>
        </div>
      </div>
      <TitleUpdateModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
        }}
      />
    </>
  );
};

export default CollectionDetailHeader;

const TitleUpdateModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
}> = ({ isOpen, onClose }) => {
  const currentCollection = useCollectionStore(state => state.currentCollection);
  const { updateCollection } = useCollectionActions();

  return (
    <CustomModal isOpen={isOpen} onClose={onClose}>
      <div className="pt-4 px-4 pb-4">
        <h4 className="pl-1">Update collection title</h4>
        <CollectionTitle
          title={currentCollection?.title || 'Untitled Collection'}
          autofocus
          placeholder="Untitled Collection"
          onComplete={onClose}
          onChange={val => {
            if (!currentCollection) return;
            void updateCollection(currentCollection.id, { title: val });
          }}
        />

        <h4 className="pl-1">Update collection description</h4>
        <CollectionTitle
          title={currentCollection?.description || ''}
          placeholder="Add a description for your collection"
          bold={false}
          onComplete={onClose}
          onChange={val => {
            if (!currentCollection) return;
            void updateCollection(currentCollection.id, { description: val });
          }}
        />
        <small className="pl-1">Click "Enter" to submit</small>
      </div>
    </CustomModal>
  );
};
